<template>
  <div class="image-editor-container">
    <!-- xiangji-image-editor iframe -->
    <iframe
      id="xiangji-image-editor"
      title="xiangji 이미지 편집기"
      style="width: 100%; height: 100%;"
      :src="getEditorUrl()"
      frameborder="0"
      @load="onIframeLoad"
    ></iframe>
    <!-- 로딩 오버레이 (타이머로 자동 해제) -->
    <div v-if="loading" class="loading-overlay">
      <div class="spinner"></div>
      <p>{{ loadingMessage }}</p>
      
      <!-- 계속 진행 버튼 - 로딩이 3초 이상 지속되면 표시 -->
      <button 
        class="continue-button"
        v-if="loadingTooLong"
        @click="hideLoading"
      >
        계속 진행하기
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ImageEditor',
  props: {
    // 번역 요청 ID
    requestId: {
      type: String,
      default: ''
    },
    // 원본 이미지 URL
    imageUrl: {
      type: String,
      default: ''
    },
    // 이미지 타입 (main, additional, detail)
    imageType: {
      type: String,
      default: 'main'
    }
  },
  data() {
    return {
      loading: true,
      loadingMessage: '이미지 에디터 로딩 중...',
      loadingTooLong: false,
      loadingTimer: null
    }
  },
  
  mounted() {
    // 편집기 메시지 리스너 등록
    window.addEventListener('message', this.handleEditorMessage);
    
    // 로딩이 오래 걸리면 자동으로 버튼 표시 (3초)
    this.loadingTimer = setTimeout(() => {
      this.loadingTooLong = true;
    }, 3000);
    
    // 안전장치: 10초 후에는 무조건 로딩 화면 숨김
    setTimeout(() => {
      this.hideLoading();
    }, 10000);
  },
  
  beforeDestroy() {
    // 이벤트 리스너와 타이머 정리
    window.removeEventListener('message', this.handleEditorMessage);
    if (this.loadingTimer) {
      clearTimeout(this.loadingTimer);
    }
  },
  
  methods: {
    /**
     * iframe 로드 완료 시 초기화
     */
    onIframeLoad() {
      console.log('iframe 로드 완료');
      
      try {
        // iframe에 메시지 전송
        const iframe = document.getElementById('xiangji-image-editor');
        if (iframe && iframe.contentWindow && this.requestId) {
          // requestId 메시지 전송
          iframe.contentWindow.postMessage(
            {
              name: "XJ_IMAGE_EDITOR_REQUESTIDS",
              requestIds: [this.requestId]
            },
            'https://www.xiangjifanyi.com'
          );
          console.log('메시지 전송 완료:', this.requestId);
          
          // 3초 후 자동으로 로딩 숨김
          setTimeout(() => {
            this.hideLoading();
          }, 3000);
        } else {
          console.warn('iframe 또는 requestId가 없음');
          this.hideLoading();
        }
      } catch (error) {
        console.error('메시지 전송 오류:', error);
        this.hideLoading();
      }
    },
    
    /**
     * 에디터 메시지 핸들러
     */
    handleEditorMessage(event) {
      // 메시지 추적을 위한 ID 생성
      const msgId = Date.now().toString(36).substring(2);
      
      console.log(`[메시지 ID: ${msgId}] 에디터 메시지 수신:`, {
        origin: event.origin,
        name: event.data?.name,
        url: event.data?.url ? '이미지 URL 포함' : '이미지 URL 없음'
      });
      
      // 메시지 형식 검사
      if (!event.data) {
        console.log(`[메시지 ID: ${msgId}] 빈 메시지 무시`);
        return;
      }
      
      // 편집된 이미지 URL을 포함한 메시지 처리
      if (event.data.name === 'XJ_IMAGE_EDITOR_URL' || event.data.url) {
        console.log(`[메시지 ID: ${msgId}] 편집 완료 메시지 수신:`, {
          hasUrl: !!event.data.url,
          hasResultUrl: !!(event.data.result && event.data.result.url),
          requestId: this.requestId || '없음'
        });
        
        // 로딩 상태 해제
        this.hideLoading();
        
        // 이미지 URL 추출 (우선순위: event.data.url > event.data.result.url > this.imageUrl)
        const imageUrl = event.data.url || 
                       (event.data.result && event.data.result.url) || 
                       this.imageUrl;
        
        console.log(`[메시지 ID: ${msgId}] 추출된 이미지 URL: ${imageUrl ? imageUrl.substring(0, 30) + '...' : '없음'}`);
        
        // 부모 컴포넌트에 결과 전달
        const emitData = {
          requestId: this.requestId,
          imageUrl: imageUrl,
          result: event.data,
          url: imageUrl,
          msgId: msgId // 메시지 추적용 ID 추가
        };
        
        console.log(`[메시지 ID: ${msgId}] 부모 컴포넌트에 이벤트 발생:`, {
          eventName: 'image-edited',
          requestId: this.requestId || '없음'
        });
        
        this.$emit('image-edited', emitData);
        return;
      }
      
      // 내부 저장 버튼 클릭 처리
      if (event.data.name === 'XJ_SAVE_CLICKED' || 
          (event.data.action && event.data.action === 'save')) {
        console.log(`[메시지 ID: ${msgId}] 저장 버튼 클릭 감지`);
        this.saveImage();
        return;
      }
      
      // 그 외 다른 메시지
      console.log(`[메시지 ID: ${msgId}] 처리되지 않은 메시지 타입:`, 
        event.data.name || event.data.action || '알 수 없음');
    },
    
    /**
     * 로딩 상태 숨기기
     */
    hideLoading() {
      this.loading = false;
    },
    
    /**
     * 외부에서 에디터 리셋
     */
    resetEditor() {
      console.log('에디터 리셋');
      
      if (!this.requestId) {
        console.warn('requestId가 없어 리셋할 수 없습니다');
        return;
      }
      
      // 로딩 상태 표시
      this.loading = true;
      this.loadingMessage = '에디터 초기화 중...';
      this.loadingTooLong = false;
      
      try {
        // iframe 새로고침
        const iframe = document.getElementById('xiangji-image-editor');
        if (iframe) {
          const currentSrc = iframe.src.split('?')[0];
          iframe.src = `${currentSrc}?t=${Date.now()}`;
          
          // 안전장치: 10초 후에는 무조건 로딩 화면 숨김
          setTimeout(() => {
            this.hideLoading();
          }, 10000);
          
          // 3초 후 버튼 표시
          this.loadingTimer = setTimeout(() => {
            this.loadingTooLong = true;
          }, 3000);
        } else {
          console.error('iframe을 찾을 수 없습니다');
          this.hideLoading();
        }
      } catch (error) {
        console.error('에디터 리셋 오류:', error);
        this.hideLoading();
      }
    },
    
    /**
     * 에디터 URL 생성
     */
    getEditorUrl() {
      const timestamp = Date.now();
      return `https://www.xiangjifanyi.com/image-editor/#/?enableCustomOCR=1&lang=KOR&logo=hidden&hideGuide=1&isCompareMode=false&t=${timestamp}`;
    },
    
    /**
     * 이미지 저장
     */
    saveImage() {
      console.log('이미지 저장 요청');
      
      const iframe = document.getElementById('xiangji-image-editor');
      if (!iframe || !iframe.contentWindow) {
        console.error('iframe을 찾을 수 없습니다');
        return;
      }
      
      try {
        // 로딩 표시
        this.loading = true;
        this.loadingMessage = '이미지 저장 중...';
        
        // 저장 메시지 전송
        iframe.contentWindow.postMessage({
          name: 'XJ_SAVE_IMAGE'
        }, 'https://www.xiangjifanyi.com');
        
        // 저장 응답이 없을 경우 기존 URL을 재사용하는 안전장치
        const saveTimeout = setTimeout(() => {
          console.log('저장 타임아웃 - 기존 이미지 URL 재사용');
          this.hideLoading();
          
          // 저장 응답이 없어도 기존 이미지 URL로 성공 이벤트 발생
          if (this.requestId) {
            this.$emit('image-edited', {
              requestId: this.requestId,
              imageUrl: this.imageUrl,
              result: { url: this.imageUrl },
              url: this.imageUrl
            });
          }
        }, 5000);
        
        // 이미지 저장 메시지 리스너 추가
        const saveMessageHandler = (event) => {
          if (event.data && (event.data.name === 'XJ_IMAGE_EDITOR_URL' || event.data.url)) {
            console.log('저장 완료 메시지 수신');
            clearTimeout(saveTimeout);
            window.removeEventListener('message', saveMessageHandler);
          }
        };
        
        window.addEventListener('message', saveMessageHandler);
      } catch (error) {
        console.error('저장 메시지 전송 오류:', error);
        this.hideLoading();
      }
    }
  }
}
</script>

<style scoped>
/* 컴포넌트 스타일 */
.image-editor-container {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 600px;
  border: 1px solid #ccc;
  border-radius: 4px;
  overflow: hidden;
}

/* 로딩 오버레이 */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

/* 로딩 스피너 */
.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 10px;
}

/* 계속 진행 버튼 */
.continue-button {
  margin-top: 20px;
  padding: 8px 16px;
  background-color: #3498db;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.continue-button:hover {
  background-color: #2980b9;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* iframe 스타일 */
#xiangji-image-editor {
  width: 100%;
  height: 100%;
  border: none;
}
</style>